import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail } from 'lucide-react';
import { data } from '@/data';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/hooks/use-toast';
import SEO from '@/components/SEO';
import { getPageSEO } from '@/config/seoConfig';
import { useTranslation } from '@/hooks/useTranslation';

const Contact = () => {
  const seoData = getPageSEO('contact');
  const t = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null
  const [lastSubmitTime, setLastSubmitTime] = useState(0);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  
  // Rate limiting: prevent submissions within 3 seconds
  const now = Date.now();
  if (now - lastSubmitTime < 3000) {
    toast({
      title: t.contact.waitMessage,
      description: t.contact.waitDescription,
      variant: "destructive",
    });
    return;
  }
  
  setIsSubmitting(true);
  setSubmitStatus(null);
  setLastSubmitTime(now);

  try {
    const encode = (data) => {
      return Object.keys(data)
        .map(
          (key) =>
            encodeURIComponent(key) + "=" + encodeURIComponent(data[key])
        )
        .join("&");
    };

    // Add unique timestamp to prevent duplicate detection
    const submissionData = {
      "form-name": "contact",
      "bot-field": "",
      "timestamp": new Date().toISOString(),
      "submission-id": Math.random().toString(36).substring(2, 15),
      ...formData,
    };

    console.log('Submitting form with data:', submissionData);

    const response = await fetch("/", {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "Cache-Control": "no-cache, no-store, must-revalidate",
        "Pragma": "no-cache",
        "Expires": "0",
      },
      body: encode(submissionData),
    });

    console.log('Response status:', response.status);
    console.log('Response headers:', response.headers);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const responseText = await response.text();
    console.log('Response received:', responseText.length > 0 ? 'Success' : 'Empty response');

    setSubmitStatus("success");
    toast({
      title: t.contact.successMessage,
      description: t.contact.successDescription,
    });

    setFormData({ name: "", email: "", message: "" });

    // Reset button status after 3 seconds
    setTimeout(() => {
      setSubmitStatus(null);
    }, 3000);

  } catch (error) {
    console.error("Form submission error:", error);
    setSubmitStatus("error");
    toast({
      title: t.contact.errorMessage,
      description: t.contact.errorDescription,
      variant: "destructive",
    });

    // Reset button status after 4 seconds for error state
    setTimeout(() => {
      setSubmitStatus(null);
    }, 4000);
  } finally {
    setIsSubmitting(false);
  }
};

  return (
    <>
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        url={seoData.url}
        image={seoData.image}
        type="website"
      />
      <div className="min-h-screen bg-gradient-to-br from-rose-50/30 to-pink-50/20 pt-32 pb-20">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="font-playfair text-4xl md:text-5xl lg:text-6xl mb-8 text-gray-800 text-center font-semibold tracking-tight"
        >
          {t.contact.title}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-center text-gray-600 text-lg mb-12 font-lora"
        >
          {t.contact.description}
        </motion.p>

        <div className="grid md:grid-cols-2 gap-12 mb-16">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-6 bg-white/60 backdrop-blur-sm p-8 rounded-2xl shadow-lg"
          >
            <div className="flex items-start space-x-4">
              <div className="bg-rose-100 p-3 rounded-full">
                <Mail className="w-6 h-6 text-rose-600" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-2 font-playfair">{t.contact.emailLabel}</h3>
                <a
                  href={`mailto:${data.musician.email}`}
                  className="text-gray-600 hover:text-gray-800 transition-colors font-lora"
                >
                  {data.musician.email}
                </a>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <form 
              onSubmit={handleSubmit} 
              className="space-y-6"
              name="contact"
              method="POST"
              data-netlify="true"
              data-netlify-honeypot="bot-field"
            >
              {/* Hidden input for Netlify */}
              <input type="hidden" name="form-name" value="contact" />
              
              {/* Hidden honeypot field for spam protection */}
              <div hidden>
                <label>
                  Don't fill this out: <input name="bot-field" onChange={handleChange} />
                </label>
              </div>

              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  {t.contact.nameLabel}
                </label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full"
                  placeholder={t.contact.namePlaceholder}
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  {t.contact.emailLabel}
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full"
                  placeholder={t.contact.emailPlaceholder}
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  {t.contact.messageLabel}
                </label>
                <Textarea
                  id="message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  rows={6}
                  className="w-full"
                  placeholder={t.contact.messagePlaceholder}
                  disabled={isSubmitting}
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-6 text-base font-medium transition-colors duration-300 ${
                  submitStatus === 'success' 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : submitStatus === 'error'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-gray-900 hover:bg-gray-800'
                } text-white`}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t.contact.submittingBtn}
                  </span>
                ) : submitStatus === 'success' ? (
                  t.contact.successMessage
                ) : submitStatus === 'error' ? (
                  'Try Again'
                ) : (
                  t.contact.submitBtn
                )}
              </Button>
              
              {submitStatus === 'success' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center text-green-600 text-sm font-medium"
                >
                  ✓ {t.contact.successDescription}
                </motion.div>
              )}
              
              {submitStatus === 'error' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center text-red-600 text-sm font-medium"
                >
                  ✗ {t.contact.errorDescription}
                </motion.div>
              )}
            </form>
          </motion.div>
        </div>
      </div>
    </div>
    </>
  );
};

export default Contact;